import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  BatteryFull,
  Camera,
  ChevronLeft,
  Mic,
  Phone,
  Plus,
  Signal,
  Video,
  Wifi,
} from "lucide-react";
import { ChannelIcon } from "../../components/channel/ChannelIcon.js";
import { LtrValue } from "../../components/common/LtrValue.js";
import { AnimatePresence, motion, useReducedMotion } from "../../lib/motion.js";
import { cn } from "../../lib/utils.js";
import { campaignLocaleAlignClass, campaignScrollAreaClass } from "./campaigns-presentation.js";

/** Physical side buttons: a device is drawn the same way in RTL and LTR. */
const SIDE_BUTTONS = [
  "left-0 top-[16%] h-[4%]",
  "left-0 top-[23%] h-[7.5%]",
  "left-0 top-[32%] h-[7.5%]",
  "right-0 top-[26%] h-[11%]",
] as const;

/**
 * Presentation-only iPhone-style WhatsApp frame. The message bubble is the real
 * WhatsAppTemplatePreview passed as children; nothing here renders template content.
 */
export function CampaignPhonePreview({
  senderName,
  senderPhone,
  contentKey,
  frameClassName = "max-w-[clamp(15rem,calc((100dvh-12rem)*0.45),18.75rem)]",
  compact = false,
  children,
}: {
  senderName: string | null;
  senderPhone: string | null;
  /** Changes when the template changes, to replay the bubble entrance. */
  contentKey: string;
  /** Width cap for the device; hosts with less vertical room than Content pass a tighter clamp. */
  frameClassName?: string;
  /**
   * Slimmer chrome and a scaled bubble for frames narrower than ~15rem, where the
   * full header (call icons, 32px avatar) would leave no room for the sender name.
   */
  compact?: boolean;
  children: ReactNode;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const uiDir = i18n.dir();
  const reduceMotion = useReducedMotion() ?? false;
  const showPhone = senderPhone != null && senderPhone !== senderName;

  return (
    <figure
      className={cn("m-0 grid justify-items-center", compact ? "gap-2" : "gap-3")}
      data-testid="campaign-phone-preview"
    >
      <div className={cn("relative w-full px-[3px]", frameClassName)}>
        {SIDE_BUTTONS.map((position) => (
          <span
            key={position}
            className={cn(
              "absolute rounded-full bg-gradient-to-b from-[#4a4d52] via-[#2a2c30] to-[#4a4d52]",
              compact ? "w-[3px]" : "w-[4px]",
              position,
            )}
            aria-hidden="true"
          />
        ))}

        <div
          className={cn(
            "bg-gradient-to-br from-[#5b5e63] via-[#2b2d31] to-[#46494e] p-[3px]",
            compact
              ? "rounded-[2.2rem] shadow-[0_24px_40px_-24px_rgb(15_23_42/0.5),0_10px_20px_-14px_rgb(15_23_42/0.3)]"
              : "rounded-[3.1rem] shadow-[0_40px_60px_-30px_rgb(15_23_42/0.55),0_18px_30px_-18px_rgb(15_23_42/0.35)]",
          )}
        >
          <div
            className={cn(
              "bg-[#0b0b0c] ring-1 ring-inset ring-white/10",
              compact ? "rounded-[2.05rem] p-[0.4rem]" : "rounded-[2.95rem] p-[0.6rem]",
            )}
          >
            <div
              className={cn(
                "relative flex aspect-[9/19.5] flex-col overflow-hidden bg-[#f6f6f6]",
                compact ? "rounded-[1.7rem]" : "rounded-[2.4rem]",
              )}
            >
              <div
                className={cn(
                  "relative flex shrink-0 items-center justify-between font-semibold text-ink [direction:ltr]",
                  compact ? "h-7 px-4 pt-0.5 text-[0.625rem]" : "h-11 px-7 pt-1 text-[0.75rem]",
                )}
                aria-hidden="true"
              >
                <span className="tabular-nums">9:41</span>
                <span
                  className={cn(
                    "absolute left-1/2 w-[32%] -translate-x-1/2 rounded-full bg-black",
                    compact ? "top-1.5 h-[1.05rem]" : "top-2.5 h-[1.65rem]",
                  )}
                />
                <span className={cn("flex items-center", compact ? "gap-0.5" : "gap-1")}>
                  <Signal size={compact ? 9 : 12} strokeWidth={2.5} />
                  <Wifi size={compact ? 9 : 12} strokeWidth={2.5} />
                  <BatteryFull size={compact ? 12 : 16} />
                </span>
              </div>

              <div
                className={cn(
                  "flex min-w-0 shrink-0 items-center border-b border-black/[0.06] text-ink",
                  compact ? "gap-1.5 px-2 pb-1.5" : "gap-2 px-2.5 pb-2 pt-0.5",
                )}
              >
                <ChevronLeft
                  size={compact ? 16 : 22}
                  className="shrink-0 text-[#007aff] rtl:rotate-180"
                  aria-hidden="true"
                />
                <span
                  className={cn(
                    "grid shrink-0 place-items-center rounded-full bg-whatsapp text-white",
                    compact ? "size-6" : "size-8",
                  )}
                  aria-hidden="true"
                >
                  <ChannelIcon platformType="WHATSAPP" size={compact ? 12 : 16} />
                </span>
                <span
                  className={cn("grid min-w-0 flex-1 gap-px", campaignLocaleAlignClass(uiDir))}
                >
                  <span
                    dir={uiDir}
                    className={cn(
                      "block w-full truncate font-semibold leading-tight",
                      compact ? "text-[0.6875rem]" : "text-[0.8125rem]",
                    )}
                    data-testid="campaign-phone-sender-name"
                  >
                    {senderName ?? t("campaigns.content.phoneNoSender")}
                  </span>
                  {showPhone ? (
                    <span
                      className={cn(
                        "block w-full truncate leading-tight text-muted",
                        compact ? "text-[0.5625rem]" : "text-[0.6875rem]",
                      )}
                    >
                      <LtrValue className="tabular-nums">{senderPhone}</LtrValue>
                    </span>
                  ) : null}
                </span>
                {compact ? null : (
                  <span className="flex shrink-0 items-center gap-3.5 pe-1 text-[#007aff]" aria-hidden="true">
                    <Video size={19} />
                    <Phone size={16} />
                  </span>
                )}
              </div>

              <div
                className={cn(
                  "grid min-h-0 flex-1 content-start bg-[#efeae2] bg-[radial-gradient(circle_at_1px_1px,rgb(0_0_0/0.045)_1px,transparent_0)]",
                  compact ? "gap-1.5 bg-[length:12px_12px] px-2 py-2" : "gap-2 bg-[length:14px_14px] px-3 py-3",
                  campaignScrollAreaClass,
                )}
                data-testid="campaign-phone-chat"
              >
                <span
                  className={cn(
                    "justify-self-center rounded-md bg-white/90 font-medium text-neutral-600 shadow-sm",
                    compact ? "px-1.5 py-px text-[0.5625rem]" : "px-2 py-0.5 text-[0.6875rem]",
                  )}
                >
                  {t("campaigns.content.phoneToday")}
                </span>
                <AnimatePresence mode="wait" initial={false}>
                  <motion.div
                    key={contentKey}
                    className={cn(
                      "min-w-0 justify-self-start",
                      compact ? "w-[94%] [zoom:0.84]" : "w-[88%]",
                    )}
                    initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: reduceMotion ? 0 : 0.16, ease: "easeOut" }}
                  >
                    {children}
                  </motion.div>
                </AnimatePresence>
              </div>

              <div
                className={cn(
                  "flex shrink-0 items-center text-[#007aff]",
                  compact ? "gap-1.5 px-2 pb-0.5 pt-1.5" : "gap-2 px-2.5 pb-1 pt-2",
                )}
                aria-hidden="true"
              >
                <Plus size={compact ? 14 : 20} className="shrink-0" />
                <span
                  className={cn(
                    "flex min-w-0 flex-1 items-center rounded-full border border-black/[0.08] bg-white text-neutral-400",
                    compact ? "h-6 px-2 text-[0.5625rem]" : "h-8 px-3 text-[0.75rem]",
                  )}
                >
                  <span className="truncate">{t("campaigns.content.phoneComposer")}</span>
                </span>
                <Camera size={compact ? 14 : 19} className="shrink-0" />
                <Mic size={compact ? 14 : 19} className="shrink-0" />
              </div>
              <div
                className={cn("grid shrink-0 place-items-center", compact ? "h-4" : "h-6")}
                aria-hidden="true"
              >
                <span
                  className={cn(
                    "w-[36%] rounded-full bg-black/85",
                    compact ? "h-[4px]" : "h-[5px]",
                  )}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <figcaption
        className={cn(
          "m-0 text-center text-subtle",
          compact ? "text-[0.65rem]" : "text-[0.7rem]",
        )}
      >
        {t("campaigns.content.previewCaption")}
      </figcaption>
    </figure>
  );
}
